<template>
  <Teleport to="body">
    <transition name="fade">
      <div v-if="show" class="petition-popup-overlay" @click="closePopup">
        <div class="petition-popup-content" @click.stop>
          <button class="close-btn" @click="closePopup" aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>

          <div class="popup-banner-section">
            <img
              :src="bannerSrc"
              alt="Bring Dr. Aafia Back - Petition"
              class="popup-banner-image"
            />
          </div>

          <div class="popup-body">
            <p class="main-message">
              Join citizens of Pakistan and humanitarians globally in a humanitarian appeal
              to Field Marshal Syed Asim Munir for the <strong>repatriation of Dr. Aafia Siddiqui</strong>.
              Sign the full petition and add your voice to the call for justice and compassion.
            </p>

            <button class="petition-btn" @click="goToPetitionPage">
              <span>Sign the Petition</span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script>
const BANNER_SRC = "/css/banner.jpg";

export default {
  name: "RamadanPopup",
  data() {
    return {
      bannerSrc: BANNER_SRC,
      show: false,
      popupTimer: null,
      isNavigating: false,
      bannerLoaded: false,
      bannerLoading: null,
    };
  },
  mounted() {
    this.preloadBannerImage();
    this.checkAndShowPopup();
  },
  beforeUnmount() {
    this.clearPopupTimer();
    document.body.style.overflow = "";
  },
  watch: {
    show(isVisible) {
      document.body.style.overflow = isVisible ? "hidden" : "";
    },
    "$route"(to) {
      if (to.name !== "home" && to.path !== "/") {
        this.clearPopupTimer();
        this.show = false;
      }
    },
  },
  methods: {
    isHomePage() {
      return this.$route.name === "home" || this.$route.path === "/";
    },
    clearPopupTimer() {
      if (this.popupTimer) {
        clearTimeout(this.popupTimer);
        this.popupTimer = null;
      }
    },
    preloadBannerImage() {
      if (this.bannerLoaded) {
        return Promise.resolve();
      }

      if (this.bannerLoading) {
        return this.bannerLoading;
      }

      this.bannerLoading = new Promise((resolve, reject) => {
        const img = new Image();
        let settled = false;

        const finish = async () => {
          if (settled) {
            return;
          }
          settled = true;

          try {
            if (img.decode) {
              await img.decode();
            }
          } catch {
            // decode can fail on some browsers; cached image is still usable
          }

          this.bannerLoaded = true;
          this.bannerLoading = null;
          resolve();
        };

        img.onload = finish;
        img.onerror = () => {
          if (settled) {
            return;
          }
          settled = true;
          this.bannerLoading = null;
          reject(new Error("Banner image failed to load"));
        };
        img.src = BANNER_SRC;

        if (img.complete) {
          finish();
        }
      });

      return this.bannerLoading;
    },
    async revealPopupWhenReady(ignoreClosedState = false) {
      if (!this.isHomePage()) {
        return;
      }

      if (!ignoreClosedState && sessionStorage.getItem("petitionPopupClosed")) {
        return;
      }

      try {
        await this.preloadBannerImage();
      } catch {
        return;
      }

      if (!this.isHomePage()) {
        return;
      }

      if (!ignoreClosedState && sessionStorage.getItem("petitionPopupClosed")) {
        return;
      }

      this.show = true;
    },
    checkAndShowPopup() {
      const wasClosed = sessionStorage.getItem("petitionPopupClosed");

      if (!this.isHomePage() || wasClosed) {
        return;
      }

      this.clearPopupTimer();
      this.popupTimer = setTimeout(() => {
        this.revealPopupWhenReady();
      }, 1005);
    },
    closePopup() {
      this.clearPopupTimer();
      this.show = false;
      sessionStorage.setItem("petitionPopupClosed", "true");
    },
    async goToPetitionPage() {
      if (this.isNavigating) {
        return;
      }

      this.isNavigating = true;
      this.closePopup();

      await this.$nextTick();

      try {
        await this.$router.push({ name: "petition" });
      } finally {
        this.isNavigating = false;
      }
    },
    openPopup() {
      if (!this.isHomePage()) {
        return;
      }

      this.clearPopupTimer();
      this.revealPopupWhenReady(true);
    },
  },
};
</script>

<style scoped>
.petition-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 20px;
  animation: overlayFadeIn 0.3s ease;
}

@keyframes overlayFadeIn {
  from {
    background: rgba(0, 0, 0, 0);
    backdrop-filter: blur(0px);
  }
  to {
    background: rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(8px);
  }
}

.petition-popup-content {
  background: #fff;
  border-radius: 24px;
  padding: 0;
  overflow: hidden;
  max-width: 620px;
  width: 100%;
  max-height: 90vh;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);
  text-align: center;
  animation: popupSlideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  display: flex;
  flex-direction: column;
}

.popup-banner-section {
  position: relative;
  width: 100%;
  line-height: 0;
}

.popup-banner-image {
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
  object-position: center center;
}

@keyframes popupSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.close-btn {
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(0, 0, 0, 0.35);
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: white;
  z-index: 20;
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.55);
  transform: rotate(90deg);
}

.popup-body {
  padding: 28px 36px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.main-message {
  font-size: 16px;
  line-height: 1.65;
  color: #4a4a4a;
  margin: 0;
  font-weight: 400;
}

.main-message strong {
  color: #198754;
  font-weight: 700;
}

.petition-btn {
  background: linear-gradient(135deg, #198754 0%, #157347 100%);
  color: white;
  border: none;
  padding: 14px 36px;
  font-size: 17px;
  font-weight: 700;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 24px rgba(25, 135, 84, 0.4);
  display: inline-flex;
  align-items: center;
  gap: 12px;
}

.petition-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 32px rgba(25, 135, 84, 0.5);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 600px) {
  .petition-popup-overlay {
    padding: 16px;
    box-sizing: border-box;
  }

  .petition-popup-content {
    max-height: 90vh;
    width: 100%;
    max-width: calc(100vw - 32px);
    border-radius: 16px;
    margin: 0 auto;
  }

  .popup-banner-image {
    width: 100%;
    height: auto;
  }

  .popup-body {
    padding: 18px 16px 20px;
    gap: 14px;
    overflow-y: auto;
  }

  .main-message {
    font-size: 14px;
  }

  .petition-btn {
    width: 100%;
    justify-content: center;
    padding: 12px 16px;
    font-size: 15px;
    box-sizing: border-box;
  }
}
</style>
